import { useEffect, useMemo, useRef, useState } from 'react'
import { UnifiedPoolData, UnifiedTokenData } from 'types/analytics'
import { poolMatchesQuery } from 'utils/analytics/poolSearch'
import { tokenMatchesQuery } from 'utils/analytics/tokenSearch'

import { TableManageBar } from './ManageBar'
import PoolsTable from './PoolsTable'
import TokensTable from './TokensTable'

const METRIC_THRESHOLD = 1_000

export type TableKind = 'pools' | 'tokens'
export type VersionView = 'v2' | 'v3'

interface Dataset<T> {
  data?: T
  isLoading?: boolean
  isFetching?: boolean
  refetch?: () => Promise<unknown>
}

interface TableViewProps {
  version: VersionView
  pools: {
    v2: Dataset<UnifiedPoolData[]>
    v3: Dataset<UnifiedPoolData[]>
  }
  tokens: {
    v2: Dataset<UnifiedTokenData[]>
    v3: Dataset<UnifiedTokenData[]>
  }
  tableKind: TableKind
  onTableKindChange: (next: TableKind) => void
}

export default function TableView({ version, pools, tokens, tableKind, onTableKindChange }: TableViewProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [hideLowTvl, setHideLowTvl] = useState(true)
  const [hideLowVolume, setHideLowVolume] = useState(true)
  const sectionRef = useRef<HTMLDivElement>(null)

  const normalizedQuery = useMemo(() => searchQuery.trim(), [searchQuery])

  useEffect(() => {
    setSearchQuery('')
  }, [version, tableKind])

  const activePools = version === 'v3' ? pools.v3 : pools.v2
  const activeTokens = version === 'v3' ? tokens.v3 : tokens.v2

  const basePoolsLoading = Boolean(activePools.data === undefined && (activePools.isLoading || activePools.isFetching))
  const baseTokensLoading = Boolean(
    activeTokens.data === undefined && (activeTokens.isLoading || activeTokens.isFetching)
  )

  const filteredPoolData = useMemo(() => {
    if (!activePools.data) return activePools.data

    const shouldFilter = hideLowTvl || hideLowVolume || normalizedQuery.length > 0
    if (!shouldFilter) return activePools.data

    return activePools.data.filter((pool) => {
      const tvlPasses = !hideLowTvl || (pool.tvlUSD ?? 0) > METRIC_THRESHOLD
      const volumePasses = !hideLowVolume || (pool.volumeUSD ?? 0) > METRIC_THRESHOLD
      if (!tvlPasses || !volumePasses) return false
      if (!normalizedQuery.length) return true
      return poolMatchesQuery(pool, normalizedQuery)
    })
  }, [activePools.data, hideLowTvl, hideLowVolume, normalizedQuery])

  const filteredTokenData = useMemo(() => {
    if (!activeTokens.data) return activeTokens.data

    const shouldFilter = hideLowTvl || hideLowVolume || normalizedQuery.length > 0
    if (!shouldFilter) return activeTokens.data

    return activeTokens.data.filter((token) => {
      const tvlPasses = !hideLowTvl || (token.tvlUSD ?? 0) > METRIC_THRESHOLD
      const volumePasses = !hideLowVolume || (token.volumeUSD ?? 0) > METRIC_THRESHOLD
      if (!tvlPasses || !volumePasses) return false
      if (!normalizedQuery.length) return true
      return tokenMatchesQuery(token, normalizedQuery)
    })
  }, [activeTokens.data, hideLowTvl, hideLowVolume, normalizedQuery])

  const isPoolsLoading = filteredPoolData === undefined
  const isTokensLoading = filteredTokenData === undefined

  const handleRefresh = async () => {
    if (isRefreshing) return
    const target = tableKind
    const refetchFn = target === 'pools' ? activePools.refetch : activeTokens.refetch
    if (!refetchFn) return

    setIsRefreshing(true)
    try {
      await refetchFn()
    } finally {
      setIsRefreshing(false)
    }
  }

  const isRefreshDisabled = Boolean(isRefreshing || (tableKind === 'pools' ? basePoolsLoading : baseTokensLoading))

  return (
    <div id="table-section" ref={sectionRef} className="flex flex-col gap-4">
      <TableManageBar
        tableView={{
          value: tableKind,
          onChange: onTableKindChange,
        }}
        hideLowTvl={{
          value: hideLowTvl,
          onChange: setHideLowTvl,
        }}
        hideLowVolume={{
          value: hideLowVolume,
          onChange: setHideLowVolume,
        }}
        searchQuery={{
          value: searchQuery,
          onChange: setSearchQuery,
        }}
        version={version}
        refreshButtonProps={{
          isLoading: isRefreshing,
          isDisabled: isRefreshDisabled,
          onClick: handleRefresh,
        }}
      />

      <div className="mt-1">
        {tableKind === 'pools' ? (
          <PoolsTable poolDatas={filteredPoolData} isLoading={isPoolsLoading} versionView={version} maxItems={20} />
        ) : (
          <TokensTable tokenDatas={filteredTokenData} isLoading={isTokensLoading} versionView={version} maxItems={20} />
        )}
      </div>
    </div>
  )
}
